"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type Answers,
  QUESTION_OPTIONS,
  type QuestionId,
  recommend,
  TEST_SLUGS,
  type TestId,
  visibleQuestions,
} from "@/lib/tool-kit/stats-distributions-more/test-selector";

/** English strings. A translated content file overrides these through `ui`. */
export const STATISTICAL_TEST_SELECTOR_STRINGS = {
  intro: "Answer the questions in order. Each answer narrows the choice.",
  restart: "Start over",
  q_goal: "What do you want to find out?",
  o_goal_compare: "Whether groups or conditions differ",
  o_goal_relate: "Whether two variables are related",
  o_goal_predict: "How well some variables predict an outcome",
  q_outcome: "What kind of outcome are you comparing?",
  o_outcome_numeric: "Numbers on a scale (scores, times, weights)",
  o_outcome_ordinal: "Ranks or ordered categories (ratings, grades)",
  o_outcome_categorical: "Categories or yes/no (counts, proportions)",
  q_groups: "How many groups or conditions?",
  o_groups_one: "One, compared with a known value",
  o_groups_two: "Two",
  o_groups_many: "Three or more",
  q_design: "How were the measurements taken?",
  o_design_independent: "Different people in each group (independent)",
  o_design_paired: "The same people measured repeatedly, or matched pairs",
  q_assumptions: "Are the usual assumptions reasonable?",
  o_assumptions_met:
    "Yes — roughly normal in each group, or about 30 or more per group, without extreme outliers",
  o_assumptions_notmet: "No — skewed, with outliers, or a small sample",
  q_sigma: "Do you know the population standard deviation (σ)?",
  o_sigma_unknown: "No, I estimate it from the sample",
  o_sigma_known: "Yes, it is given or known from the population",
  q_factors: "How many factors define the groups?",
  o_factors_one: "One (for example, treatment)",
  o_factors_two: "Two (for example, treatment and sex)",
  q_categories: "How many categories can the outcome take?",
  o_categories_two: "Two (pass/fail, yes/no)",
  o_categories_many: "Three or more",
  q_expected: "Is every expected count in the table at least 5?",
  o_expected_ok: "Yes, or the sample is large",
  o_expected_small: "No — some expected counts are small",
  q_relateKinds: "What kind of variables are they?",
  o_relateKinds_numeric: "Both are numbers on a scale",
  o_relateKinds_ordinal: "One or both are ranks or ordered categories",
  o_relateKinds_categorical: "Both are categories",
  q_relateAssumptions:
    "Is the relationship roughly a straight line, without extreme outliers?",
  o_relateAssumptions_met: "Yes",
  o_relateAssumptions_notmet: "No — curved, skewed or with outliers",
  q_predictOutcome: "What are you trying to predict?",
  o_predictOutcome_numeric: "A number on a scale",
  o_predictOutcome_binary: "A yes/no outcome",
  o_predictOutcome_other: "A count, an ordered category or several categories",
  q_predictors: "How many predictors?",
  o_predictors_one: "One",
  o_predictors_several: "Two or more",
  w_goal_compare:
    "You want to know whether groups or conditions differ, so you need a test of a difference.",
  w_goal_relate:
    "You want to know whether two variables go together, so you need a correlation or an association test.",
  w_goal_predict:
    "You want to predict an outcome from other variables, so you need a regression model.",
  w_outcome_numeric:
    "A measured outcome allows a test of means, provided its assumptions hold.",
  w_outcome_ordinal:
    "Ranked or ordinal data are best analysed with rank-based tests, which do not need normality.",
  w_outcome_categorical:
    "A categorical outcome means comparing counts or proportions rather than means.",
  w_groups_one: "One group against a known value calls for a one-sample test.",
  w_groups_two: "Two groups or conditions call for a two-sample test.",
  w_groups_many:
    "Three or more groups need one overall test first; running many pairwise tests inflates false positives.",
  w_design_independent:
    "Different people in each group make the groups independent, so no pairing is used.",
  w_design_paired:
    "Repeated or matched measurements are related, and the test must use that pairing.",
  w_assumptions_met:
    "Normality, or a large enough sample, supports the parametric test, which has more power.",
  w_assumptions_notmet:
    "Skew, outliers or a small sample undermine the parametric test, so a rank-based one is safer.",
  w_sigma_unknown:
    "Estimating σ from the sample means using the t distribution.",
  w_sigma_known:
    "A known σ lets you use the normal distribution directly: a z test.",
  w_factors_one: "One grouping factor is a one-way analysis.",
  w_factors_two:
    "Two factors need a two-way analysis, which also tests their interaction.",
  w_categories_two:
    "A two-category outcome against a hypothesised proportion is an exact binomial test.",
  w_categories_many:
    "Several categories against hypothesised proportions is a chi-square goodness-of-fit test.",
  w_expected_ok:
    "With expected counts of 5 or more the chi-square approximation is reliable.",
  w_expected_small:
    "Small expected counts make chi-square unreliable; an exact test does not rely on the approximation.",
  w_relateKinds_numeric:
    "Two measured variables suggest a correlation of values.",
  w_relateKinds_ordinal:
    "Ranks carry order but not distance, so a rank correlation fits.",
  w_relateKinds_categorical:
    "Two categorical variables are tested for association in a table of counts.",
  w_relateAssumptions_met:
    "A linear relationship without outliers suits Pearson's r.",
  w_relateAssumptions_notmet:
    "Curvature, skew or outliers distort Pearson's r; a rank correlation is robust to them.",
  w_predictOutcome_numeric:
    "A numeric outcome is modelled with linear regression.",
  w_predictOutcome_binary:
    "A yes/no outcome is modelled with logistic regression, which predicts odds.",
  w_predictOutcome_other:
    "Counts, ordered and multi-category outcomes need other models, which this site has no calculator for yet.",
  w_predictors_one: "One predictor is simple linear regression.",
  w_predictors_several:
    "Several predictors need multiple regression, which adjusts each for the others.",
  resultHeading: "Recommended test",
  outsideNote:
    "That goal needs a model this selector does not cover — for counts, Poisson or negative binomial regression; for ordered outcomes, ordinal logistic regression; for several categories, multinomial logistic regression. A statistics package or a statistician can help.",
  reasonsHeading: "Why this test",
  alternativesHeading: "Alternatives",
  checksHeading: "Check the assumptions first",
  followUpsHeading: "After the test",
  incomplete: "Answer the question above to see the recommended test.",
  test_ttest: "T-test (one-sample, independent or paired)",
  test_ztest: "Z-test for means",
  test_sign: "Sign test",
  test_wilcoxon: "Wilcoxon signed-rank test",
  test_mannwhitney: "Mann–Whitney U test",
  test_anova: "One-way ANOVA",
  test_twoway: "Two-way ANOVA",
  test_tukey: "Tukey HSD post hoc test",
  test_kruskal: "Kruskal–Wallis test",
  test_rmanova: "Repeated-measures ANOVA",
  test_friedman: "Friedman test",
  test_binomial: "Binomial test",
  test_chisq: "Chi-square test",
  test_fisher: "Fisher's exact test",
  test_twoprop: "Two-proportion z-test",
  test_mcnemar: "McNemar's test",
  test_cochran: "Cochran's Q test",
  test_pearson: "Pearson correlation",
  test_spearman: "Spearman correlation",
  test_kendall: "Kendall's tau",
  test_linear: "Linear regression",
  test_multiple: "Multiple regression",
  test_logistic: "Logistic regression",
  test_shapiro: "Shapiro–Wilk normality test",
  test_effect: "Effect size calculator",
  test_apa: "APA statistics formatter",
  test_power: "Power analysis",
} as const;

type Strings = typeof STATISTICAL_TEST_SELECTOR_STRINGS;

/** A pre-set path, so the page opens on a worked answer. */
const START: Answers = {
  goal: "compare",
  outcome: "numeric",
  groups: "two",
  design: "independent",
  assumptions: "met",
};

function text(strings: Strings, key: string): string {
  return (strings as Record<string, string>)[key] ?? key;
}

function TestLinks({
  tests,
  strings,
}: {
  tests: readonly TestId[];
  strings: Strings;
}) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      {tests.map((test) => (
        <li key={test}>
          <Link
            className="text-primary underline underline-offset-2"
            href={`/tools/${TEST_SLUGS[test]}`}
          >
            {text(strings, `test_${test}`)}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function StatisticalTestSelectorWidget() {
  const strings = useToolUi(STATISTICAL_TEST_SELECTOR_STRINGS);
  const [answers, setAnswers] = useState<Answers>(START);
  const { shown } = visibleQuestions(answers);
  const result = recommend(answers);

  return (
    <>
      <section className="space-y-5">
        <p className="text-muted-foreground text-sm">{strings.intro}</p>
        {shown.map((question: QuestionId) => (
          <PillGroup
            key={question}
            label={text(strings, `q_${question}`)}
            name={`ts-${question}`}
            onChange={(value) =>
              setAnswers((old) => ({ ...old, [question]: value }))
            }
            options={QUESTION_OPTIONS[question].map((value) => ({
              value,
              label: text(strings, `o_${question}_${value}`),
            }))}
            value={answers[question] ?? ""}
          />
        ))}
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setAnswers({})}
          type="button"
        >
          {strings.restart}
        </button>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultHeading}</h2>
        {result === null ? <Notice>{strings.incomplete}</Notice> : null}
        {result?.primary === null ? (
          <Notice>{strings.outsideNote}</Notice>
        ) : null}
        {result?.primary ? (
          <div className={`${CARD} space-y-4`}>
            <p className="font-semibold text-2xl text-foreground">
              <Link
                className="text-primary underline underline-offset-2"
                href={`/tools/${TEST_SLUGS[result.primary]}`}
              >
                {text(strings, `test_${result.primary}`)}
              </Link>
            </p>
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.reasonsHeading}</h3>
              <ol className="list-decimal space-y-1 pl-5 text-muted-foreground text-sm">
                {result.reasons.map((reason) => (
                  <li key={reason.question}>
                    {text(strings, `w_${reason.question}_${reason.answer}`)}
                  </li>
                ))}
              </ol>
            </div>
            {result.checks.length > 0 ? (
              <div className="space-y-2">
                <h3 className={WIDGET_H3}>{strings.checksHeading}</h3>
                <TestLinks strings={strings} tests={result.checks} />
              </div>
            ) : null}
            {result.alternatives.length > 0 ? (
              <div className="space-y-2">
                <h3 className={WIDGET_H3}>{strings.alternativesHeading}</h3>
                <TestLinks strings={strings} tests={result.alternatives} />
              </div>
            ) : null}
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.followUpsHeading}</h3>
              <TestLinks strings={strings} tests={result.followUps} />
            </div>
          </div>
        ) : null}
      </section>
    </>
  );
}
